import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Icon, Prompt, PromptActions, PromptBody, Title } from 'admination-react'
import { verifyPassword } from '@/core/api/auth'
import { ApiError } from '@/core/api/http'
import { CredentialField } from '@/core/components/auth/CredentialField'
import { tooManyAttemptsMessage } from '@/core/components/auth/messages'

/** The signed-in coach re-enters their password before the device leaves the Teilnehmersicht. */
export function PasswortPrompt({ titel, bestaetigen, onBestaetigt, onClose }: {
  titel: string
  bestaetigen: string
  onBestaetigt: () => void
  onClose: () => void
}) {
  const { t } = useTranslation('vergabeStaff')
  const [passwort, setPasswort] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const feld = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!busy) feld.current?.focus()
  }, [busy])

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (busy) return
    if (!passwort) {
      setError(t('anwesenheit.passwortFalsch'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await verifyPassword(passwort)
    } catch (err) {
      setPasswort('')
      setError(
        tooManyAttemptsMessage(err)
          ?? (err instanceof ApiError && err.status === 400 ? t('anwesenheit.passwortFalsch') : t('anwesenheit.passwortFehler')),
      )
      setBusy(false)
      return
    }
    onBestaetigt()
  }

  return (
    <Prompt onEscape={onClose} busy={busy} aria-labelledby="teilnehmersicht-passwort-title">
      <PromptBody>
        <Title as="h3" id="teilnehmersicht-passwort-title">
          {titel}
        </Title>
        <form id="teilnehmersicht-passwort-form" onSubmit={(e) => void submit(e)} noValidate className="section-body">
          <CredentialField ref={feld} label={t('anwesenheit.passwort')} id="teilnehmersicht-passwort" name="password" type="password"
            autoComplete="current-password" value={passwort} onChange={setPasswort} disabled={busy} />
          {error && (
            <p role="alert" className="field-error">
              {error}
            </p>
          )}
        </form>
      </PromptBody>
      <PromptActions>
        <Button onClick={onClose} disabled={busy}>
          {t('anwesenheit.abbrechen')}
        </Button>
        <Button type="submit" form="teilnehmersicht-passwort-form" variant="primary" loading={busy}>
          <Icon name="i-check" />{bestaetigen}
        </Button>
      </PromptActions>
    </Prompt>
  )
}
